import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "./api";
import { CATEGORIES } from "./constants";
import InterviewFlow from "./components/interview/InterviewFlow";

// ─── Utility Components ──────────────────────────────────────────

function CategoryPill({ catId, small }) {
  const cat = CATEGORIES.find((c) => c.id === catId);
  if (!cat) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: small ? 3 : 5,
        padding: small ? "2px 8px" : "3px 10px",
        borderRadius: 20,
        fontSize: small ? 10 : 11,
        fontWeight: 600,
        background: cat.color + "22",
        color: cat.color,
        border: `1px solid ${cat.color}44`,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
      }}
    >
      <span style={{ fontSize: small ? 10 : 12 }}>{cat.icon}</span>
      {cat.label}
    </span>
  );
}

function ProgressBar({ value, max, color = "#6B9BD2", height = 6 }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ width: "100%", height, background: "#1a1a2e", borderRadius: height / 2, overflow: "hidden" }}>
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          background: `linear-gradient(90deg, ${color}, ${color}cc)`,
          borderRadius: height / 2,
          transition: "width 0.6s cubic-bezier(0.23, 1, 0.32, 1)",
        }}
      />
    </div>
  );
}

function QRPlaceholder({ label, size = 64 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        background: "#fff",
        borderRadius: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid #333",
        flexShrink: 0,
      }}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1, marginBottom: 2 }}>
        {[1, 1, 0, 1, 0, 1, 0, 1, 1].map((v, i) => (
          <div key={i} style={{ width: size / 8, height: size / 8, background: v ? "#000" : "#fff" }} />
        ))}
      </div>
      <span style={{ fontSize: 6, color: "#333", fontWeight: 700 }}>{label?.slice(0, 8)}</span>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <div style={{
        width: 32, height: 32, border: "3px solid #333",
        borderTopColor: "#F0C75E", borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorMessage({ message, onRetry }) {
  return (
    <div style={{
      padding: 20, background: "#E8927C22", border: "1px solid #E8927C44",
      borderRadius: 12, textAlign: "center", color: "#E8927C",
    }}>
      <div style={{ marginBottom: 10 }}>{message}</div>
      {onRetry && (
        <button onClick={onRetry} style={{
          padding: "8px 16px", borderRadius: 8, border: "1px solid #E8927C",
          background: "transparent", color: "#E8927C", cursor: "pointer",
        }}>
          Retry
        </button>
      )}
    </div>
  );
}

// ─── Search Overlay ──────────────────────────────────────────────

function SearchOverlay({ visible, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (visible && inputRef.current) {
      inputRef.current.focus();
    }
    if (!visible) {
      setQuery("");
      setResults([]);
    }
  }, [visible]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await api("GET", `/items/search?q=${encodeURIComponent(query)}`);
        setResults(data.items || []);
      } catch (err) {
        console.error("Search error:", err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(8, 8, 20, 0.85)",
        backdropFilter: "blur(12px)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: 80,
      }}
      onClick={onClose}
    >
      <div style={{ width: "90%", maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "#16162a",
            border: "2px solid #F0C75E",
            borderRadius: 16,
            padding: "14px 20px",
            boxShadow: "0 0 40px rgba(240, 199, 94, 0.15)",
          }}
        >
          <span style={{ fontSize: 22 }}>{"\u{1F50D}"}</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Where is my ___? (e.g. "pipe wrench")'
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "#F0F0F0",
              fontSize: 17,
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
          {loading && <span style={{ fontSize: 14 }}>{"\u231B"}</span>}
          <kbd style={{
            padding: "3px 8px",
            borderRadius: 6,
            background: "#252545",
            color: "#888",
            fontSize: 11,
            border: "1px solid #333",
          }}>ESC</kbd>
        </div>

        {results.length > 0 && (
          <div
            style={{
              marginTop: 12,
              background: "#16162a",
              border: "1px solid #252545",
              borderRadius: 14,
              overflow: "hidden",
              maxHeight: 400,
              overflowY: "auto",
            }}
          >
            {results.map((item, i) => (
              <div
                key={item.id}
                style={{
                  padding: "14px 20px",
                  borderBottom: i < results.length - 1 ? "1px solid #1f1f3a" : "none",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <div>
                  <div style={{ color: "#F0F0F0", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                    {item.name}
                    {item.quantity > 1 && <span style={{ color: "#888", fontWeight: 400 }}> × {item.quantity}</span>}
                  </div>
                  <div style={{ color: "#888", fontSize: 12 }}>
                    {"\u{1F4E6}"} {item.container_name || "Unknown"} → {"\u{1F4CD}"} {item.location_name || "Unknown"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {(item.categories || []).slice(0, 2).map((c) => (
                    <CategoryPill key={c} catId={c} small />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {query.length > 1 && !loading && results.length === 0 && (
          <div style={{
            marginTop: 12, padding: 24, textAlign: "center",
            background: "#16162a", borderRadius: 14, border: "1px solid #252545",
            color: "#666", fontSize: 14,
          }}>
            No items matching "{query}" — try a different term
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Inventory View ──────────────────────────────────────────────

function ContainerCard({ container, expanded, onToggle, onRefresh }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (expanded && items.length === 0) {
      setLoading(true);
      api("GET", `/containers/${container.id}/items`)
        .then((data) => setItems(data.items || []))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [expanded, container.id, items.length]);

  const lowStockItems = items.filter(
    (i) => i.is_consumable && i.quantity <= (i.low_stock_threshold || 0)
  );
  const totalItems = container.item_count || items.reduce((s, i) => s + i.quantity, 0);
  const typeIcon = container.type === "bag" ? "\u{1F392}" : container.type === "bin" ? "\u{1F4E6}" : "\u{1F5C4}\uFE0F";

  return (
    <div
      style={{
        background: expanded ? "#16162a" : "#12122a",
        border: `1px solid ${expanded ? "#333" : "#1f1f3a"}`,
        borderRadius: 16,
        overflow: "hidden",
        transition: "all 0.3s ease",
      }}
    >
      <div
        onClick={onToggle}
        style={{
          padding: "16px 20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 14,
        }}
      >
        <div style={{
          width: 56, height: 56, borderRadius: 12,
          background: "linear-gradient(135deg, #1a1a3e, #252550)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 26, flexShrink: 0,
          border: "1px solid #333",
        }}>
          {typeIcon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ color: "#F0F0F0", fontWeight: 700, fontSize: 15 }}>{container.name}</span>
            {lowStockItems.length > 0 && (
              <span style={{
                background: "#E8927C33",
                color: "#E8927C",
                fontSize: 10,
                fontWeight: 700,
                padding: "2px 7px",
                borderRadius: 8,
              }}>
                {lowStockItems.length} LOW
              </span>
            )}
          </div>
          <div style={{ color: "#777", fontSize: 12, marginBottom: 6 }}>
            {"\u{1F4CD}"} {container.location_name || "Unknown location"}
          </div>
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ color: "#F0F0F0", fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{totalItems}</div>
          <div style={{ color: "#666", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>items</div>
        </div>

        <QRPlaceholder label={container.qr_code || container.id} size={48} />

        <span style={{
          color: "#555", fontSize: 18,
          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.3s ease",
        }}>{"\u25BE"}</span>
      </div>

      {expanded && (
        <div style={{ borderTop: "1px solid #1f1f3a" }}>
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              {items.map((item, i) => {
                const isLow = item.is_consumable && item.quantity <= (item.low_stock_threshold || 0);
                return (
                  <div
                    key={item.id}
                    style={{
                      padding: "10px 20px 10px 90px",
                      borderBottom: i < items.length - 1 ? "1px solid #151530" : "none",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: isLow ? "#E8927C08" : "transparent",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: isLow ? "#E8927C" : "#ccc", fontSize: 13, fontWeight: 500 }}>
                        {item.name}
                      </span>
                      {item.is_consumable && (
                        <span style={{
                          fontSize: 9, color: "#666", background: "#1a1a3e",
                          padding: "1px 6px", borderRadius: 4, textTransform: "uppercase",
                        }}>consumable</span>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{
                        color: isLow ? "#E8927C" : "#888",
                        fontSize: 13, fontWeight: 600, fontVariantNumeric: "tabular-nums",
                      }}>
                        ×{item.quantity}
                      </span>
                      {isLow && <span style={{ fontSize: 10, color: "#E8927C" }}>{"\u26A0\uFE0F"}</span>}
                    </div>
                  </div>
                );
              })}
              <div style={{
                padding: "10px 20px",
                display: "flex", gap: 8,
                borderTop: "1px solid #1f1f3a",
              }}>
                <button style={{
                  flex: 1, padding: "8px", borderRadius: 8,
                  background: "#1a1a3e", border: "1px solid #333",
                  color: "#aaa", fontSize: 12, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}>+ Add Item</button>
                <button style={{
                  flex: 1, padding: "8px", borderRadius: 8,
                  background: "#1a1a3e", border: "1px solid #333",
                  color: "#aaa", fontSize: 12, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}>{"\u{1F4F7}"} Photo</button>
                <button style={{
                  flex: 1, padding: "8px", borderRadius: 8,
                  background: "#1a1a3e", border: "1px solid #333",
                  color: "#aaa", fontSize: 12, cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                }}>{"\u{1F3F7}\uFE0F"} Print Label</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function InventoryView() {
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState(null);
  const [containers, setContainers] = useState([]);
  const [stats, setStats] = useState({ total_tools: 0, total_consumables: 0, low_stock_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInterview, setShowInterview] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [containersData, dashboardData] = await Promise.all([
        api("GET", `/containers${filter ? `?type=${filter}` : ""}`),
        api("GET", "/dashboard"),
      ]);
      setContainers(containersData.containers || []);
      setStats(dashboardData.stats || { total_tools: 0, total_consumables: 0 });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Interview mode
  if (showInterview) {
    return (
      <div
        style={{
          background: "#12122a",
          border: "1px solid #1f1f3a",
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        <InterviewFlow
          onComplete={() => {
            setShowInterview(false);
            loadData();
          }}
          onCancel={() => setShowInterview(false)}
        />
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={loadData} />;

  return (
    <div>
      {/* Stats Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Tools", value: stats.total_tools || 0, icon: "\u{1F527}", color: "#6B9BD2" },
          { label: "Consumables", value: stats.total_consumables || 0, icon: "\u{1F4E6}", color: "#7ECEC1" },
          { label: "Low Stock", value: stats.low_stock_count || (stats.low_stock?.length || 0), icon: "\u26A0\uFE0F", color: "#E8927C" },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              background: "#12122a",
              border: "1px solid #1f1f3a",
              borderRadius: 14,
              padding: "14px 16px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 20, marginBottom: 4 }}>{stat.icon}</div>
            <div style={{ color: stat.color, fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{stat.value}</div>
            <div style={{ color: "#666", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Category Filter */}
      <div style={{
        display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16,
        padding: "12px 0", borderBottom: "1px solid #1f1f3a",
      }}>
        <button
          onClick={() => setFilter(null)}
          style={{
            padding: "5px 12px", borderRadius: 20, border: "1px solid",
            borderColor: !filter ? "#F0C75E" : "#333",
            background: !filter ? "#F0C75E22" : "transparent",
            color: !filter ? "#F0C75E" : "#777",
            fontSize: 11, fontWeight: 600, cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          All
        </button>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setFilter(cat.id === filter ? null : cat.id)}
            style={{
              padding: "5px 12px", borderRadius: 20, border: "1px solid",
              borderColor: filter === cat.id ? cat.color : "#333",
              background: filter === cat.id ? cat.color + "22" : "transparent",
              color: filter === cat.id ? cat.color : "#777",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Add Container Button */}
      <button
        onClick={() => setShowInterview(true)}
        style={{
          width: "100%",
          padding: "16px 20px",
          marginBottom: 16,
          borderRadius: 14,
          border: "2px dashed #F0C75E44",
          background: "linear-gradient(135deg, #F0C75E11, #E8927C11)",
          color: "#F0C75E",
          fontSize: 15,
          fontWeight: 600,
          cursor: "pointer",
          fontFamily: "'DM Sans', sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
          transition: "all 0.2s ease",
        }}
      >
        <span style={{ fontSize: 20 }}>{"\u{1F4F7}"}</span>
        Add Container with Photo
      </button>

      {/* Container List */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {containers.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#666" }}>
            No containers found. Use the button above to add your first container!
          </div>
        ) : (
          containers.map((c) => (
            <ContainerCard
              key={c.id}
              container={c}
              expanded={expanded === c.id}
              onToggle={() => setExpanded(expanded === c.id ? null : c.id)}
              onRefresh={loadData}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Projects View ───────────────────────────────────────────────

function ProjectCard({ project, onTaskToggle }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("tasks");
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && !details) {
      setLoading(true);
      api("GET", `/projects/${project.id}`)
        .then(setDetails)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [open, project.id, details]);

  const tasks = details?.tasks || [];
  const purchases = details?.purchases || [];
  const done = project.completed_tasks || tasks.filter((t) => t.is_done).length;
  const total = project.total_tasks || tasks.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const statusColor = project.status === "in_progress" ? "#6B9BD2" : "#F0C75E";

  const totalPurchaseCost = purchases.reduce((s, p) => s + (p.estimated_cost || 0), 0);
  const purchasedCost = purchases.filter((p) => p.is_purchased).reduce((s, p) => s + (p.actual_cost || p.estimated_cost || 0), 0);

  return (
    <div style={{
      background: "#12122a", border: "1px solid #1f1f3a",
      borderRadius: 16, overflow: "hidden",
    }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ padding: "16px 20px", cursor: "pointer" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <div>
            <div style={{ color: "#F0F0F0", fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
              {project.name}
            </div>
            <div style={{ color: "#777", fontSize: 12 }}>
              {"\u{1F4CD}"} {project.zone_name || "Unknown"} → {project.room_name || "Unknown"}
            </div>
          </div>
          <span style={{
            padding: "4px 10px", borderRadius: 8,
            background: statusColor + "22", color: statusColor,
            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
          }}>
            {project.status?.replace("_", " ") || "planned"}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <ProgressBar value={done} max={total || 1} color={statusColor} height={8} />
          </div>
          <span style={{ color: "#888", fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
            {done}/{total} tasks · {pct}%
          </span>
        </div>

        {project.budget_estimated && (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, color: "#666", fontSize: 12 }}>
            <span>Budget: ${project.budget_estimated.toLocaleString()}</span>
            <span>Spent: <span style={{ color: (project.budget_spent || 0) > project.budget_estimated * 0.8 ? "#E8927C" : "#7ECEC1" }}>
              ${(project.budget_spent || 0).toLocaleString()}
            </span></span>
          </div>
        )}
      </div>

      {open && (
        <div style={{ borderTop: "1px solid #1f1f3a" }}>
          <div style={{ display: "flex", borderBottom: "1px solid #1f1f3a" }}>
            {["tasks", "purchases"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  flex: 1, padding: "10px", border: "none",
                  background: tab === t ? "#1a1a3e" : "transparent",
                  color: tab === t ? "#F0F0F0" : "#666",
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                  textTransform: "uppercase", letterSpacing: "0.05em",
                  fontFamily: "'DM Sans', sans-serif",
                  borderBottom: tab === t ? `2px solid ${statusColor}` : "2px solid transparent",
                }}
              >
                {t === "tasks" ? `Tasks (${total})` : `Purchases (${purchases.length})`}
              </button>
            ))}
          </div>

          {loading ? (
            <LoadingSpinner />
          ) : tab === "tasks" ? (
            <div>
              {tasks.map((task) => (
                <div
                  key={task.id}
                  style={{
                    padding: "10px 20px",
                    borderBottom: "1px solid #151530",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    opacity: task.is_done ? 0.5 : 1,
                  }}
                >
                  <div
                    onClick={() => onTaskToggle?.(task.id, !task.is_done)}
                    style={{
                      width: 20, height: 20, borderRadius: 6,
                      border: `2px solid ${task.is_done ? "#7ECEC1" : "#333"}`,
                      background: task.is_done ? "#7ECEC122" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#7ECEC1", fontSize: 12, flexShrink: 0,
                      cursor: "pointer",
                    }}
                  >
                    {task.is_done && "\u2713"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      color: "#ddd", fontSize: 13,
                      textDecoration: task.is_done ? "line-through" : "none",
                    }}>
                      {task.title}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 3 }}>
                      {task.assignee_name && (
                        <span style={{ fontSize: 10, color: "#888" }}>{"\u{1F464}"} {task.assignee_name}</span>
                      )}
                      {task.time_estimate_minutes && (
                        <span style={{ fontSize: 10, color: "#888" }}>{"\u23F1"} {Math.round(task.time_estimate_minutes / 60)}h</span>
                      )}
                      {task.category_label && <CategoryPill catId={task.category_id} small />}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div>
              {purchases.map((p, i) => (
                <div
                  key={p.id || i}
                  style={{
                    padding: "10px 20px",
                    borderBottom: "1px solid #151530",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    opacity: p.is_purchased ? 0.5 : 1,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{
                      color: p.is_purchased ? "#7ECEC1" : "#F0C75E",
                      fontSize: 14,
                    }}>
                      {p.is_purchased ? "\u2713" : "\u25CB"}
                    </span>
                    <span style={{
                      color: "#ddd", fontSize: 13,
                      textDecoration: p.is_purchased ? "line-through" : "none",
                    }}>
                      {p.item_name}
                    </span>
                  </div>
                  <span style={{
                    color: p.is_purchased ? "#666" : "#F0C75E",
                    fontSize: 13, fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    ${p.estimated_cost || 0}
                  </span>
                </div>
              ))}
              <div style={{
                padding: "12px 20px",
                borderTop: "1px solid #1f1f3a",
                display: "flex", justifyContent: "space-between",
                color: "#888", fontSize: 12,
              }}>
                <span>Total: ${totalPurchaseCost}</span>
                <span>Purchased: ${purchasedCost} / Remaining: ${totalPurchaseCost - purchasedCost}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectsView() {
  const [projects, setProjects] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [projectsData, maintenanceData] = await Promise.all([
        api("GET", "/projects"),
        api("GET", "/maintenance/upcoming?days=60"),
      ]);
      setProjects(projectsData.projects || []);
      setMaintenance(maintenanceData.tasks || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTaskToggle = async (taskId, isDone) => {
    try {
      await api("PATCH", `/tasks/${taskId}`, { is_done: isDone });
      loadData();
    } catch (err) {
      console.error("Failed to update task:", err);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} onRetry={loadData} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {projects.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "#666" }}>
          No projects found. Create one to get started!
        </div>
      ) : (
        projects.map((p) => (
          <ProjectCard key={p.id} project={p} onTaskToggle={handleTaskToggle} />
        ))
      )}

      {/* Maintenance Schedule */}
      <div style={{
        background: "#12122a", border: "1px solid #1f1f3a",
        borderRadius: 16, padding: "16px 20px", marginTop: 8,
      }}>
        <div style={{
          color: "#F0F0F0", fontWeight: 700, fontSize: 14, marginBottom: 12,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          {"\u{1F504}"} Recurring Maintenance
        </div>
        {maintenance.length === 0 ? (
          <div style={{ color: "#666", fontSize: 13, padding: "8px 0" }}>
            No upcoming maintenance tasks
          </div>
        ) : (
          maintenance.map((m) => {
            const daysUntil = Math.ceil(m.days_until_due || 0);
            const urgent = daysUntil <= 14;

            return (
              <div
                key={m.id}
                style={{
                  padding: "8px 0",
                  borderBottom: "1px solid #151530",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ color: "#ddd", fontSize: 13, fontWeight: 500 }}>{m.task_name}</div>
                  <div style={{ color: "#666", fontSize: 11, marginTop: 2 }}>
                    {m.frequency} {m.category_icon && <span>{m.category_icon}</span>}
                  </div>
                </div>
                <span style={{
                  color: urgent ? "#E8927C" : "#888",
                  fontSize: 11, fontWeight: 600,
                }}>
                  {daysUntil <= 0 ? "OVERDUE" : `${daysUntil}d`}
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Kit Builder View ────────────────────────────────────────────

function KitBuilderView() {
  const [jobType, setJobType] = useState("toilet replacement");
  const [kitResult, setKitResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const jobTypes = [
    { id: "toilet replacement", name: "Toilet Replacement", category: "plumbing" },
    { id: "install outlet", name: "Replace Outlet/Switch", category: "electrical" },
    { id: "patch drywall", name: "Patch Drywall", category: "drywall" },
  ];

  const buildKit = async () => {
    setLoading(true);
    try {
      const data = await api("POST", "/kits/build", { job_type: jobType });
      setKitResult(data);
    } catch (err) {
      console.error("Failed to build kit:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    buildKit();
  }, [jobType]);

  const selectedJob = jobTypes.find((j) => j.id === jobType);
  const cat = CATEGORIES.find((c) => c.id === selectedJob?.category);

  const tools = kitResult?.items?.filter((i) => !i.is_consumable) || [];
  const consumables = kitResult?.items?.filter((i) => i.is_consumable) || [];
  const missingTools = tools.filter((t) => !t.have);
  const missingConsumables = consumables.filter((c) => !c.have);

  return (
    <div>
      {/* Job Selector */}
      <div style={{
        display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap",
      }}>
        {jobTypes.map((job) => {
          const jobCat = CATEGORIES.find((c) => c.id === job.category);
          return (
            <button
              key={job.id}
              onClick={() => setJobType(job.id)}
              style={{
                padding: "10px 16px", borderRadius: 12, cursor: "pointer",
                border: `2px solid ${jobType === job.id ? jobCat?.color || "#F0C75E" : "#1f1f3a"}`,
                background: jobType === job.id ? (jobCat?.color || "#F0C75E") + "15" : "#12122a",
                color: jobType === job.id ? "#F0F0F0" : "#888",
                fontSize: 13, fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {jobCat?.icon} {job.name}
            </button>
          );
        })}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : kitResult?.items?.length > 0 ? (
        <>
          {/* Kit Contents */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Tools */}
            <div style={{
              background: "#12122a", border: "1px solid #1f1f3a",
              borderRadius: 16, padding: "16px 20px",
            }}>
              <div style={{ color: "#F0F0F0", fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
                {"\u{1F527}"} Tools Needed
              </div>
              {tools.map((t, i) => (
                <div key={i} style={{
                  padding: "8px 0",
                  borderBottom: "1px solid #151530",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <div>
                    <div style={{ color: t.have ? "#7ECEC1" : "#E8927C", fontSize: 13, fontWeight: 500 }}>
                      {t.have ? "\u2713" : "\u2717"} {t.needed}
                    </div>
                    {t.have && t.inventory_match?.container_name && (
                      <div style={{ color: "#666", fontSize: 10, marginTop: 2 }}>{"\u{1F4E6}"} {t.inventory_match.container_name}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Consumables */}
            <div style={{
              background: "#12122a", border: "1px solid #1f1f3a",
              borderRadius: 16, padding: "16px 20px",
            }}>
              <div style={{ color: "#F0F0F0", fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
                {"\u{1F4E6}"} Consumables / Parts
              </div>
              {consumables.map((c, i) => (
                <div key={i} style={{
                  padding: "8px 0",
                  borderBottom: "1px solid #151530",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <div>
                    <div style={{ color: c.have ? "#7ECEC1" : "#F0C75E", fontSize: 13, fontWeight: 500 }}>
                      {c.have ? "\u2713" : "\u25CB"} {c.needed}
                    </div>
                    {c.have && c.inventory_match?.container_name && (
                      <div style={{ color: "#666", fontSize: 10, marginTop: 2 }}>{"\u{1F4E6}"} {c.inventory_match.container_name}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Purchase Summary */}
          {kitResult?.shopping_list?.length > 0 && (
            <div style={{
              marginTop: 12, background: "#F0C75E11",
              border: "1px solid #F0C75E33", borderRadius: 14,
              padding: "14px 20px",
            }}>
              <div style={{ color: "#F0C75E", fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
                {"\u{1F6D2}"} Shopping List for this Job
              </div>
              <div style={{ color: "#ccc", fontSize: 12, lineHeight: 1.8 }}>
                {kitResult.shopping_list.join(" · ")}
              </div>
              <div style={{ color: "#F0C75E", fontSize: 12, fontWeight: 600, marginTop: 8 }}>
                Missing: {kitResult.missing_count} items
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: 40, color: "#666" }}>
          No kit data available. Try defining kits in the database.
        </div>
      )}
    </div>
  );
}

// ─── Main App ────────────────────────────────────────────────────

export default function HomeBaseApp() {
  const [view, setView] = useState("inventory");
  const [searchOpen, setSearchOpen] = useState(false);
  const [apiStatus, setApiStatus] = useState("checking");

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    api("GET", "/dashboard")
      .then(() => setApiStatus("connected"))
      .catch(() => setApiStatus("disconnected"));
  }, []);

  const navItems = [
    { id: "inventory", label: "Inventory", icon: "\u{1F4E6}" },
    { id: "projects", label: "Projects", icon: "\u{1F4CB}" },
    { id: "kits", label: "Kit Builder", icon: "\u{1F9F0}" },
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(180deg, #0a0a1a 0%, #0d0d24 100%)",
      color: "#F0F0F0",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Space+Mono:wght@700&display=swap" rel="stylesheet" />

      <SearchOverlay visible={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Header */}
      <div style={{
        padding: "20px 24px 0",
        maxWidth: 900,
        margin: "0 auto",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 20,
        }}>
          <div>
            <h1 style={{
              margin: 0, fontSize: 28, fontWeight: 800,
              fontFamily: "'Space Mono', monospace",
              background: "linear-gradient(135deg, #F0C75E, #E8927C)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-0.02em",
            }}>
              HomeBase
            </h1>
            <div style={{ color: "#555", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", marginTop: 2 }}>
              Tool &amp; Project Command Center
            </div>
          </div>

          <button
            onClick={() => setSearchOpen(true)}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 16px", borderRadius: 10,
              background: "#16162a", border: "1px solid #252545",
              color: "#777", fontSize: 13, cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {"\u{1F50D}"} Search
            <kbd style={{
              padding: "2px 6px", borderRadius: 4,
              background: "#0d0d24", fontSize: 10, border: "1px solid #333",
            }}>{"\u2318"}K</kbd>
          </button>
        </div>

        {/* Nav */}
        <div style={{
          display: "flex", gap: 4,
          background: "#0d0d24",
          borderRadius: 14,
          padding: 4,
          border: "1px solid #1a1a3a",
          marginBottom: 20,
        }}>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setView(item.id)}
              style={{
                flex: 1,
                padding: "10px 0",
                borderRadius: 10,
                border: "none",
                background: view === item.id
                  ? "linear-gradient(135deg, #1a1a3e, #252550)"
                  : "transparent",
                color: view === item.id ? "#F0F0F0" : "#666",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
                transition: "all 0.2s ease",
                boxShadow: view === item.id ? "0 2px 8px rgba(0,0,0,0.3)" : "none",
              }}
            >
              {item.icon} {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 40px" }}>
        {view === "inventory" && <InventoryView />}
        {view === "projects" && <ProjectsView />}
        {view === "kits" && <KitBuilderView />}
      </div>

      {/* API Status Badge */}
      <div style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        background: "linear-gradient(135deg, #1a1a3e, #252550)",
        border: "1px solid #333",
        borderRadius: 14,
        padding: "10px 16px",
        display: "flex", alignItems: "center", gap: 10,
        cursor: "pointer",
        boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: "50%",
          background: apiStatus === "connected" ? "#7ECEC1" : apiStatus === "checking" ? "#F0C75E" : "#E8927C",
          boxShadow: `0 0 8px ${apiStatus === "connected" ? "#7ECEC1" : apiStatus === "checking" ? "#F0C75E" : "#E8927C"}`,
          animation: "pulse 2s infinite",
        }} />
        <span style={{ color: "#ccc", fontSize: 12, fontWeight: 600 }}>
          {apiStatus === "connected" ? "API Connected" : apiStatus === "checking" ? "Connecting..." : "API Offline"}
        </span>
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.4; }
          }
        `}</style>
      </div>
    </div>
  );
}
